import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from './ui/input';
import { Check, AlertTriangle, ChevronDown } from 'lucide-react';

const norm = (s) => (s || '').trim().toLowerCase();

/**
 * Autocomplete input backed by the Product Catalog. Shows matching suggestions
 * as the user types and lets them click one, while still allowing free text.
 * When a free-typed value isn't in the catalog, a gentle "double-check" nudge
 * is shown to keep product/manufacturer names consistent for stock checks.
 */
export const ProductInput = ({ value, onChange, options = [], placeholder, testId, nudgeLabel, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const wrapRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const uniqueOptions = useMemo(
    () => Array.from(new Set((options || []).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [options],
  );
  const filtered = useMemo(
    () => uniqueOptions.filter((o) => norm(o).includes(norm(query))).slice(0, 20),
    [uniqueOptions, query],
  );

  const inList = uniqueOptions.some((o) => norm(o) === norm(value));
  const showNudge = !!(value && value.trim() && uniqueOptions.length > 0 && !inList);

  const select = (o) => { onChange(o); setQuery(o); setOpen(false); };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          placeholder={placeholder}
          data-testid={testId}
          autoComplete="off"
          disabled={disabled}
          className="pr-8"
        />
        {uniqueOptions.length > 0 && (
          <ChevronDown
            className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
          />
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto"
          data-testid={`${testId}-options`}
        >
          {filtered.map((o, i) => (
            <li
              key={o}
              onMouseDown={(e) => { e.preventDefault(); select(o); }}
              className="px-3 py-1.5 text-sm hover:bg-blue-50 cursor-pointer flex items-center justify-between gap-2"
              data-testid={`${testId}-option-${i}`}
            >
              <span className="truncate">{o}</span>
              {norm(o) === norm(value) && <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
            </li>
          ))}
        </ul>
      )}
      {showNudge && (
        <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1" data-testid={`${testId}-nudge`}>
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          {nudgeLabel || 'Not in the catalog — please double-check before saving.'}
        </p>
      )}
    </div>
  );
};

export default ProductInput;
